import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Pathname } from 'Routes';
import { AppContext } from 'Context';
import { axios, baseUrl, getToken } from 'Api';
import styles from './ViewerInvitePopup.module.css';

// Popup shown on the dashboard after a user opens a /viewer/invite/:id link.
// The invite id arrives as a `?viewerInvite=` query param (set by the
// ViewerInvite landing route). We fetch the inviter's account counts and let
// the user accept (become their viewer) or dismiss the invite.
export const ViewerInvitePopup = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAppSnackbar } = useContext(AppContext);
  const [inviteId, setInviteId] = useState('');
  const [details, setDetails] = useState(null);
  const [visible, setVisible] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const id = searchParams.get('viewerInvite');
    if (!id) return;
    setInviteId(id);

    const fetchCounts = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/getAccountCounts`,
          { token: getToken(), id },
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (response.data?.success && response.data?.data) {
          setDetails(response.data.data);
          setVisible(true);
        }
      } catch (error) {
        console.error('Failed to fetch account counts:', error);
      }
    };
    fetchCounts();
  // eslint-disable-next-line
  }, []);

  // Clear the param once the popup is open so a refresh won't re-trigger it.
  useEffect(() => {
    if (!visible) return;
    if (!searchParams.get('viewerInvite')) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('viewerInvite');
    setSearchParams(nextParams, { replace: true });
  }, [visible]);

  const handleClose = () => setVisible(false);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await axios.post(
        `${baseUrl}/addAsViewerBySelf`,
        { token: getToken(), id: inviteId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setAppSnackbar({
        isVisible: true,
        type: response.data?.success ? 'success' : 'error',
        message: response.data?.message || 'Something went wrong. Please try again.',
      });
      if (response.data?.success) {
        setVisible(false);
        navigate(Pathname.access);
      }
    } catch (error) {
      console.error('Failed to accept viewer invite:', error);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Could not accept the invite. Please try again.',
      });
    } finally {
      setAccepting(false);
    }
  };

  if (!visible || !details) return null;

  const fullName = [details.firstname, details.lastname]
    .filter(Boolean)
    .join(' ') || details.email;
  const videos = Number(details.videosCount) || 0;
  const photos = Number(details.photosCount) || 0;

  return (
    <div
      className={styles.overlay}
      role="button"
      tabIndex={0}
      onClick={handleClose}
      onKeyDown={() => {}}
    >
      <div
        className={styles.modal}
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={() => {}}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close"
          disabled={accepting}
        >
          ×
        </button>

        <div className={styles.avatar}>
          {details.image ? (
            <img
              src={details.image}
              alt={fullName}
              className={styles.avatarImage}
            />
          ) : (
            (details.firstname?.[0] || details.email?.[0] || '?').toUpperCase()
          )}
        </div>

        <h2 className={styles.title}>
          <span className={styles.name}>{fullName}</span>
          {' '}
          invited to view their content
        </h2>

        <p className={styles.email}>{details.email}</p>

        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{videos}</span>
            <span className={styles.statLabel}>{videos === 1 ? 'Video' : 'Videos'}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{photos}</span>
            <span className={styles.statLabel}>{photos === 1 ? 'Photo' : 'Photos'}</span>
          </div>
        </div>

        <p className={styles.subtext}>
          Accept to start watching everything they share with you.
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleClose}
            disabled={accepting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.acceptButton}
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? 'Accepting…' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};
